import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  StatusIndicatorAppearance,
  StatusIndicatorComponent,
  type StatusVariant,
} from '../status-indicator/status-indicator';
import {
  getSubscriptionStatusMeaning,
  getSubscriptionStatusVariant,
} from '../../utils/organization.utils';
import type { SubscriptionState } from '../../models';

/**
 * Subscription column/chip for managed customer org rows and the
 * customer-detail header. Centralises variant resolution so the customers
 * list grid, list table, and detail header all read the same tint / icon
 * for the same underlying state.
 *
 * When `subscriptionState` is supplied the richer discriminator wins so
 * past_due / checkout_failed render as `error` and pending_setup renders
 * as `pending` — otherwise falls back to the boolean-only mapping.
 *
 * Semantic markers distinguish operational activity, setup waiting and
 * billing failures independently of their tone. Billing identity belongs
 * to the surrounding column or section rather than the state marker.
 */
@Component({
  selector: 'app-customer-subscription-indicator',
  imports: [StatusIndicatorComponent],
  templateUrl: './customer-subscription-indicator.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSubscriptionIndicatorComponent {
  readonly label = input.required<string>();
  readonly hasActiveSubscription = input<boolean | null | undefined>();
  readonly subscriptionState = input<SubscriptionState | null | undefined>();
  readonly appearance = input<StatusIndicatorAppearance>('inline');

  protected readonly variant = computed<StatusVariant>(() =>
    getSubscriptionStatusVariant(this.hasActiveSubscription(), this.subscriptionState())
  );

  protected readonly meaning = computed(() =>
    getSubscriptionStatusMeaning(this.hasActiveSubscription(), this.subscriptionState())
  );
}
